import {
  CustomCollectionsStepAssets,
  CustomCollectionsFieldConfig,
  CustomFieldConfig,
  FinancialBrand,
  OnboardingApplication,
} from "@dreambigger/shared/src/types";
import isCustomFieldSection from "@dreambigger/shared/src/utils/isCustomFieldSection";
import { Form } from "@dreambigger/design-system/src/components";
import { Checkbox } from "antd";
import { FormInstance } from "antd/lib/form";
import CustomField from "../../../components/CustomField";
import shouldDisplayField from "../utils/shouldDisplayField";
import getDisplayDependencyKeys from "../utils/getDisplayDependencyKeys";

/** Wrapper that reactively shows/hides a field based on display conditions */
function ConditionalCustomField({
  field,
  index,
  form,
  brand,
  application,
  allFields,
}: {
  field: CustomFieldConfig;
  index: number;
  form: FormInstance;
  brand: FinancialBrand;
  application?: OnboardingApplication;
  allFields?: CustomFieldConfig[];
}) {
  if (!field.display || field.display.length === 0) {
    return (
      <CustomField
        field={field}
        index={index}
        form={form}
        brand={brand}
        application={application}
      />
    );
  }

  // Extract which form fields this condition depends on so we only
  // re-render when those specific values change (not on every form change).
  // This prevents infinite loops caused by field mount/unmount triggering
  // form state changes which trigger shouldUpdate on all wrappers.
  const depKeys = getDisplayDependencyKeys(field, allFields);

  return (
    <Form.Item
      noStyle
      shouldUpdate={(prev, cur) => depKeys.some((k) => prev[k] !== cur[k])}
    >
      {() => {
        const values = form.getFieldsValue(true);
        if (
          !shouldDisplayField(field, values, application, undefined, allFields)
        )
          return null;
        return (
          <CustomField
            field={field}
            index={index}
            form={form}
            brand={brand}
            application={application}
          />
        );
      }}
    </Form.Item>
  );
}

/** Group customFields entries into buckets separated by inline section markers.
 *  Fields before the first marker land in a leading bucket with title=null.
 *  Empty buckets are preserved here; the renderer auto-hides them. */
function groupByInlineSections(
  entries: NonNullable<CustomCollectionsStepAssets["customFields"]>
): { title: string | null; fields: CustomCollectionsFieldConfig[] }[] {
  const groups: {
    title: string | null;
    fields: CustomCollectionsFieldConfig[];
  }[] = [{ title: null, fields: [] }];
  for (const entry of entries) {
    if (isCustomFieldSection(entry)) {
      groups.push({ title: entry.section, fields: [] });
    } else {
      groups[groups.length - 1].fields.push(entry);
    }
  }
  return groups;
}

/** Render custom fields with inline section markers and hidden field support */
export default function SectionedCustomFieldGrid({
  customFields,
  hiddenFieldKeys,
  form,
  brand,
  application,
}: {
  customFields: NonNullable<CustomCollectionsStepAssets["customFields"]>;
  hiddenFieldKeys?: Set<string>;
  form: FormInstance;
  brand: FinancialBrand;
  application?: OnboardingApplication;
}) {
  // Split field configs (ignoring markers) into visible + hidden buckets.
  const fieldConfigs = customFields.filter(
    (e): e is CustomCollectionsFieldConfig => !isCustomFieldSection(e)
  );
  const hiddenFields = hiddenFieldKeys
    ? fieldConfigs.filter((f) => hiddenFieldKeys.has(f.fieldKey))
    : [];

  // Hidden fields still need to be registered in the form so their values
  // are available to display condition evaluation via form.getFieldsValue().
  const hiddenFormItems = hiddenFields.map((f) => (
    <Form.Item
      key={f.fieldKey}
      name={f.fieldKey}
      hidden
      noStyle
      valuePropName={f.fieldType === "checkbox" ? "checked" : undefined}
    >
      {f.fieldType === "checkbox" ? (
        <Checkbox style={{ display: "none" }} />
      ) : (
        <input type="hidden" />
      )}
    </Form.Item>
  ));

  const groups = groupByInlineSections(customFields).map((g) => ({
    title: g.title,
    fields: hiddenFieldKeys
      ? g.fields.filter((f) => !hiddenFieldKeys.has(f.fieldKey))
      : g.fields,
  }));

  // Collect all unique dependency keys across visible conditional fields.
  // Used by group wrappers to re-evaluate visibility only when relevant values change.
  const allDepKeys = new Set<string>();
  for (const g of groups) {
    for (const field of g.fields) {
      for (const k of getDisplayDependencyKeys(field, fieldConfigs)) {
        allDepKeys.add(k);
      }
    }
  }
  const depKeysArray = Array.from(allDepKeys);

  return (
    <>
      {hiddenFormItems}
      {groups.map((group, gIdx) => {
        if (group.fields.length === 0) return null;

        return (
          <Form.Item
            key={group.title ?? `__leading-${gIdx}`}
            noStyle
            shouldUpdate={(prev, cur) =>
              depKeysArray.some((k) => prev[k] !== cur[k])
            }
          >
            {() => {
              const values = form.getFieldsValue(true);
              const hasVisible = group.fields.some((f) =>
                shouldDisplayField(
                  f,
                  values,
                  application,
                  undefined,
                  fieldConfigs
                )
              );
              if (!hasVisible) return null;

              return (
                <div className={gIdx > 0 ? "mt-1" : ""}>
                  {group.title && (
                    <p
                      className="f-1 ttu gray-6 fwb mb-3"
                      style={{ letterSpacing: "0.05em" }}
                      // HTML is allowed so admins can inline links, <br />,
                      // or emphasis inside section titles — mirrors how every
                      // other text slot in acquire-flows handles content
                      // (StepContentHeader, CustomField, OptionCard, etc.).
                      dangerouslySetInnerHTML={{ __html: group.title }}
                    />
                  )}
                  {group.fields.map((field, i) => (
                    <ConditionalCustomField
                      key={field.fieldKey}
                      field={field}
                      index={i}
                      form={form}
                      brand={brand}
                      application={application}
                      allFields={fieldConfigs}
                    />
                  ))}
                </div>
              );
            }}
          </Form.Item>
        );
      })}
    </>
  );
}
